/**
 * The Channels screen's primary status matrix.
 *
 * Every supported channel owns exactly one compact row. Binding setup detail
 * belongs in the Bindings manager; this matrix answers whether each channel is
 * ready and what it is doing now.
 */

import React, { useMemo, useState } from 'react';
import { HelpCircle, Maximize } from 'lucide-react';
import { Panel, Button } from '@cloudsam/ui';
import ChannelHealthReport from './ChannelHealthReport';
import { CHANNEL_HEALTH_LABELS, channelHealthResult } from '../utils/channelHealthDisplay';

const EMPTY = '—';

function meteringLabel(channel) {
    if (channel.comingSoon) return 'Coming soon';
    return channel.metered ? 'Metered' : 'No per-message cost';
}

function valueOrDash(value) {
    if (value === null || value === undefined || value === '' || value === 0) return EMPTY;
    return typeof value === 'number' ? value.toLocaleString() : value;
}

function bindingCount(bindings) {
    if (!bindings.length) return `${EMPTY} bindings`;
    return `${bindings.length.toLocaleString()} binding${bindings.length === 1 ? '' : 's'}`;
}

function strictSum(bindings, key) {
    if (!bindings.length || bindings.some((binding) => binding[key] == null)) return null;
    return bindings.reduce((sum, binding) => sum + Number(binding[key]), 0);
}

function statusSummary(bindings) {
    const labels = bindings.map((binding) => binding.status?.label).filter(Boolean);
    if (!bindings.length || labels.length !== bindings.length) return null;
    const counts = labels.reduce((all, label) => {
        all[label] = (all[label] || 0) + 1;
        return all;
    }, {});
    const toneOrder = ['red', 'yellow', 'neutral', 'green'];
    const tone = toneOrder.find((candidate) => bindings.some((binding) => binding.status?.tone === candidate)) || 'neutral';
    return {
        tone,
        label: Object.entries(counts)
            .map(([label, count]) => labels.length === 1 || count === 1 ? label : `${count} ${label}`)
            .join(' · '),
    };
}

function credentialSummary(bindings) {
    if (!bindings.length || bindings.some((binding) => !binding.credential?.text)) return null;
    if (bindings.length === 1) return bindings[0].credential;

    const groups = [
        ['red', 'expired'],
        ['yellow', 'expiring'],
        ['green', 'valid'],
        ['neutral', 'unverified'],
    ];
    const parts = groups.flatMap(([tone, label]) => {
        const count = bindings.filter((binding) => binding.credential?.tone === tone).length;
        return count ? [`${count} ${label}`] : [];
    });
    const tone = groups.find(([candidate]) => bindings.some((binding) => binding.credential?.tone === candidate))?.[0] || 'neutral';
    return parts.length ? { tone, text: parts.join(' · ') } : null;
}

function inboundSummary(bindings) {
    if (!bindings.length || bindings.some((binding) => !binding.inbound?.text || binding.inbound.text === 'n/a')) return null;
    if (bindings.length === 1) return bindings[0].inbound;
    const stale = bindings.filter((binding) => binding.inbound?.stale).length;
    if (stale) return { text: `${stale} need attention`, note: 'check platform configuration', stale: true };
    return { text: `${bindings.length} reporting`, note: 'webhooks healthy', stale: false };
}

function usageSummary(bindings) {
    const surveys = strictSum(bindings, 'surveysSent');
    const messages = strictSum(bindings, 'messagesSent');
    if (surveys == null || messages == null) {
        const knownSurveys = bindings.reduce((sum, binding) => sum + Number(binding.knownSurveysSent || 0), 0);
        const knownMessages = bindings.reduce((sum, binding) => sum + Number(binding.knownMessagesSent || 0), 0);
        if (knownSurveys === 0 && knownMessages === 0) return null;
        return `At least ${knownSurveys.toLocaleString()} survey${knownSurveys === 1 ? '' : 's'} · ${knownMessages.toLocaleString()} msg${knownMessages === 1 ? '' : 's'}`;
    }
    if (surveys === 0 && messages === 0) return null;
    return `${surveys.toLocaleString()} surveys · ${messages.toLocaleString()} msgs`;
}

function spendSummary(channel, bindings) {
    if (!channel.metered || channel.comingSoon) return null;
    const spend = strictSum(bindings, 'spendValue');
    if (spend == null || spend === 0) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(spend);
}

function EmptyCell() {
    return <span className="matrix-empty">{EMPTY}</span>;
}

function requestLabel(state) {
    if (state === 'pending') return 'Request pending';
    if (state === 'acknowledged') return 'Request acknowledged';
    if (state === 'declined') return 'Request declined';
    if (state === 'cancelled') return 'Request cancelled';
    if (state === 'approved') return 'Request approved';
    return '';
}

function ChannelHelp({ channel, hasBindings }) {
    return (
        <details className="channel-help">
            <summary aria-label={`About ${channel.name}`} title={`About ${channel.name}`}>
                <HelpCircle size={14} strokeWidth={1.5} aria-hidden="true" />
            </summary>
            <div className="channel-help__popup" role="tooltip">
                <strong>{channel.name}</strong>
                <span>{channel.capability}</span>
                {!hasBindings && <span>{channel.slotNote}</span>}
                <span>Manage this organization's channel accounts from Bindings.</span>
            </div>
        </details>
    );
}

export default function ChannelsList({
    rows = [],
    search,
    onSearch,
    footerQueued,
    footerActive,
    requestingEnablement,
    onRequestEnablement,
}) {
    const total = useMemo(
        () => rows.reduce((sum, row) => sum + (row.bindings ? row.bindings.length : 0), 0),
        [rows]
    );
    // Which channel's health report is open, by id. The reading itself comes
    // from the row already on screen, so opening the report fetches nothing.
    const [reportFor, setReportFor] = useState(null);
    const reportRow = rows.find((row) => row.channel.id === reportFor) || null;

    const actions = (
        <>
            <input
                className="input channels-filter"
                placeholder="Filter this view"
                title="Filters the rows shown here only"
                value={search}
                onChange={onSearch}
                aria-label="Filter this view"
            />
            <Button variant="ghost">Export ▾</Button>
            <Button variant="ghost" iconOnly aria-label="Open full screen" title="Open full screen">
                <Maximize size={16} strokeWidth={1.5} />
            </Button>
        </>
    );
    const hasLiveData = footerQueued != null || footerActive != null;
    const totalLabel = total ? `${total} binding${total === 1 ? '' : 's'}` : `${EMPTY} bindings`;

    return (
        <Panel
            title="Channel status"
            count={`${rows.length} channels · ${totalLabel}`}
            className="channels-list-panel"
            actions={actions}
        >
            <div className="channels-scroll">
                <table className="channels-matrix" aria-label="Channel status">
                    <thead>
                        <tr className="channels-col-head">
                            <th scope="col">Channel</th>
                            <th scope="col">Status</th>
                            <th scope="col">Credential</th>
                            <th scope="col">Inbound</th>
                            <th scope="col">Queued</th>
                            <th scope="col">Active</th>
                            <th scope="col">Usage</th>
                            <th scope="col">Spend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ channel, bindings = [], control = {} }) => {
                            const status = statusSummary(bindings);
                            const credential = credentialSummary(bindings);
                            const inbound = inboundSummary(bindings);
                            const exactQueued = strictSum(bindings, 'queued');
                            const knownQueued = bindings.reduce((sum, binding) => sum + Number(binding.knownQueued || 0), 0);
                            const queued = exactQueued == null && knownQueued > 0 ? `${knownQueued.toLocaleString()}+` : exactQueued;
                            const active = strictSum(bindings, 'active');
                            const usage = usageSummary(bindings);
                            const spend = spendSummary(channel, bindings);
                            const enabled = control.enabled;
                            const healthResult = channelHealthResult(control);
                            const health = CHANNEL_HEALTH_LABELS[healthResult];
                            const enablementRequest = control.enablement_request?.state || 'none';
                            const requestStatus = requestLabel(enablementRequest);
                            const requestPending = enablementRequest === 'pending' || requestingEnablement === channel.id;

                            return (
                                <tr
                                    key={channel.id}
                                    className={`channel-matrix-row${enabled === false ? ' is-org-disabled' : ''}${enabled == null ? ' is-enablement-unknown' : ''}`}
                                >
                                    <th className="channel-cell" scope="row">
                                        <span className="channel-summary">
                                            {/* The mark and the name are one button, not two. They read as a
                                                single label, and splitting them would put two tab stops and two
                                                identical announcements on one thing. */}
                                            <button
                                                type="button"
                                                className="channel-identity"
                                                onClick={() => setReportFor(channel.id)}
                                                title={`Health report for ${channel.name}`}
                                                aria-label={`Open the health report for ${channel.name}`}
                                            >
                                                <span className="channel-mark" style={{ background: channel.markColor }} aria-hidden="true">
                                                    {channel.abbr}
                                                </span>
                                            </button>
                                            <span className="channel-summary__copy">
                                                <span className="channel-summary__name">
                                                    <button
                                                        type="button"
                                                        className="channel-name channel-name--button"
                                                        onClick={() => setReportFor(channel.id)}
                                                        title={`Health report for ${channel.name}`}
                                                    >
                                                        {channel.name}
                                                    </button>
                                                    <ChannelHelp channel={channel} hasBindings={bindings.length > 0} />
                                                </span>
                                                <span className="channel-metadata">
                                                    <span>{meteringLabel(channel)}</span>
                                                    {channel.metered && !channel.comingSoon && (
                                                        <button type="button" className="link-inline">Rate schedule</button>
                                                    )}
                                                    <span>{bindingCount(bindings)}</span>
                                                    <span
                                                        className={`runtime-health runtime-health--${healthResult}`}
                                                        title={healthResult === 'failed' && control.runtime_health_reason
                                                            ? control.runtime_health_reason
                                                            : `Channel health: ${health}`}
                                                    >
                                                        Health: {health}
                                                    </span>
                                                    {enabled === false && (
                                                        <span className="enablement-request-controls">
                                                            {requestStatus && (
                                                                <span className="enablement-request-state">{requestStatus}</span>
                                                            )}
                                                            {!['pending', 'acknowledged', 'approved'].includes(enablementRequest) && (
                                                            <button
                                                                type="button"
                                                                className="link-inline request-enablement"
                                                                disabled={requestPending}
                                                                onClick={() => onRequestEnablement?.(channel.id)}
                                                            >
                                                                {requestPending ? 'Request pending' : 'Request enablement'}
                                                            </button>
                                                            )}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </span>
                                    </th>

                                    <td className="matrix-cell">
                                        {enabled === false ? (
                                            <span className="stack enablement-status">
                                                <span className="tag tag--neutral">Disabled</span>
                                                <span className="binding-note">Binding: {status?.label || EMPTY}</span>
                                            </span>
                                        ) : enabled == null ? (
                                            <span className="stack enablement-status">
                                                <span className="tag tag--neutral">Enablement unknown</span>
                                                <span className="binding-note">Binding: {status?.label || EMPTY}</span>
                                            </span>
                                        ) : status ? <span className={`tag tag--${status.tone}`}>{status.label}</span> : <EmptyCell />}
                                    </td>
                                    <td className="matrix-cell">
                                        {credential ? (
                                            <span className="row-gap">
                                                <span className={`dot dot--${credential.tone}`} />
                                                <span className="binding-cell">{credential.text}</span>
                                            </span>
                                        ) : <EmptyCell />}
                                    </td>
                                    <td className="matrix-cell">
                                        {inbound ? (
                                            <span className="stack">
                                                <span className={`binding-cell${inbound.stale ? ' binding-cell--stale' : ''}`}>{inbound.text}</span>
                                                {inbound.note && <span className="binding-note">{inbound.note}</span>}
                                            </span>
                                        ) : <EmptyCell />}
                                    </td>
                                    <td className="matrix-cell matrix-cell--number">{valueOrDash(queued)}</td>
                                    <td className="matrix-cell matrix-cell--number">{valueOrDash(active)}</td>
                                    <td className="matrix-cell matrix-cell--usage">{usage || <EmptyCell />}</td>
                                    <td className="matrix-cell matrix-cell--spend">{spend || <EmptyCell />}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {reportRow && (
                <ChannelHealthReport
                    channel={reportRow.channel}
                    control={reportRow.control}
                    onClose={() => setReportFor(null)}
                />
            )}

            <div className="panel-footer panel-footer--channels">
                <div className="channels-footer-status">
                    <span>{valueOrDash(footerQueued)} queued · {valueOrDash(footerActive)} in flight</span>
                    {hasLiveData ? (
                        <>
                            <span className="dot dot--green" />
                            <span>live · refreshes every 60s</span>
                        </>
                    ) : <span>data unavailable</span>}
                </div>
            </div>
        </Panel>
    );
}
